import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, GripVertical, Layers, X } from "lucide-react";
import { useContent } from "@/context/ContentContext";

export default function AdminSection({ title, icon: Icon, badge, defaultOpen = false, testid, onMove, dragProps, stack, children }) {
  const c = useContent();
  const persisted = testid && c?.isAdmin ? c?.content?.[`admin.section.open.${testid}`] : null;
  const [open, setOpen] = useState(persisted != null ? persisted === "1" : defaultOpen);
  useEffect(() => {
    if (persisted != null) setOpen(persisted === "1");
  }, [persisted]);
  const toggleOpen = () => {
    const v = !open;
    setOpen(v);
    if (testid && c?.isAdmin) c.save(`admin.section.open.${testid}`, v ? "1" : "0", { quiet: true });
  };
  const [dragEnabled, setDragEnabled] = useState(false);
  const d = dragProps || {};
  return (
    <div
      className={`relative border bg-pb-surface/50 mb-4 transition-all ${stack ? "mt-4 border-pb-cyan/40" : ""} ${d.isDragging ? "opacity-40 border-pb-cyan border-dashed" : d.isOver ? "border-pb-cyan shadow-[0_-3px_0_0_#22D3EE]" : stack ? "" : "border-pb-border"}`}
      data-testid={testid}
      draggable={dragEnabled}
      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", testid); d.onDragStart?.(); }}
      onDragEnd={() => { setDragEnabled(false); d.onDragEnd?.(); }}
      onDragOver={d.onDragOver}
      onDrop={(e) => { setDragEnabled(false); d.onDrop?.(e); }}>
      {stack && (
        <>
          <div className="absolute -top-1.5 left-2 right-2 h-1.5 border border-b-0 border-pb-cyan/30 bg-pb-surface/80 pointer-events-none" />
          <div className="absolute -top-3 left-4 right-4 h-1.5 border border-b-0 border-pb-cyan/20 bg-pb-surface/60 pointer-events-none" />
        </>
      )}
      <div className="w-full flex items-center gap-1 pr-4 hover:bg-pb-elevated/60 transition-colors">
        {d.onDragStart && (
          <span data-testid={`${testid}-drag-handle`} title="Hold and slide to move this section"
            onMouseDown={() => setDragEnabled(true)} onMouseUp={() => setDragEnabled(false)}
            className="cursor-grab active:cursor-grabbing text-zinc-600 hover:text-pb-cyan shrink-0 pl-3 py-4">
            <GripVertical className="w-5 h-5" />
          </span>
        )}
        <button data-testid={`${testid}-toggle`} onClick={toggleOpen}
          className={`flex items-center gap-3 flex-1 min-w-0 py-4 text-left ${d.onDragStart ? "pl-1" : "pl-5"}`}>
          {Icon && <Icon className="w-5 h-5 text-pb-red shrink-0" />}
          <span className="font-display font-bold text-lg uppercase flex-1 truncate">{title}</span>
          {badge != null && badge !== 0 && (
            <span className="bg-pb-red text-black text-xs font-bold px-2 py-0.5" data-testid={`${testid}-badge`}>{badge}</span>
          )}
          <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
        {onMove && (
          <span className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
            <span role="button" data-testid={`${testid}-move-up`} onClick={() => onMove(testid, -1)} title="Move section up"
              className="w-7 h-7 border border-pb-border text-zinc-500 hover:border-pb-cyan hover:text-pb-cyan flex items-center justify-center cursor-pointer transition-colors">
              <ChevronUp className="w-4 h-4" />
            </span>
            <span role="button" data-testid={`${testid}-move-down`} onClick={() => onMove(testid, 1)} title="Move section down"
              className="w-7 h-7 border border-pb-border text-zinc-500 hover:border-pb-cyan hover:text-pb-cyan flex items-center justify-center cursor-pointer transition-colors">
              <ChevronDown className="w-4 h-4" />
            </span>
          </span>
        )}
      </div>
      {open && <div className="px-5 pb-5 border-t border-pb-border/50 pt-5 animate-fade-up">{children}</div>}
    </div>
  );
}

export function OrderedSections({ storageKey = "admin.sections.order", groupsKey = "admin.sections.groups", filter, children }) {
  const c = useContent();
  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);
  const [combining, setCombining] = useState(false);
  const [sel, setSel] = useState([]);
  const [stackName, setStackName] = useState("");
  let kids = React.Children.toArray(children).filter(Boolean);
  if (filter) kids = kids.filter((k) => filter(k.props.testid));
  const kidByKey = {};
  kids.forEach((k) => { kidByKey[k.props.testid] = k; });
  let groups = [];
  try { groups = (JSON.parse(c?.content?.[groupsKey] || "[]") || []).filter((g) => g && g.id && Array.isArray(g.members)); } catch { groups = []; }
  groups = groups.map((g) => ({ ...g, members: g.members.filter((m) => kidByKey[m]) })).filter((g) => g.members.length > 0);
  const groupedKeys = new Set(groups.flatMap((g) => g.members));
  const units = [
    ...groups.map((g) => ({ key: g.id, group: g })),
    ...kids.filter((k) => !groupedKeys.has(k.props.testid)).map((k) => ({ key: k.props.testid, kid: k })),
  ];
  let order = [];
  try { order = JSON.parse(c?.content?.[storageKey] || "[]"); } catch { order = []; }
  const baseKeys = units.map((u) => u.key);
  const idx = (key) => { const i = order.indexOf(key); return i === -1 ? 1000 + baseKeys.indexOf(key) : i; };
  const sorted = [...units].sort((a, b) => idx(a.key) - idx(b.key));
  const saveOrder = (cur) => c.save(storageKey, JSON.stringify(cur), { quiet: true });
  const saveGroups = (gs) => c.save(groupsKey, JSON.stringify(gs), { quiet: true });
  const move = (key, dir) => {
    const cur = sorted.map((u) => u.key);
    const i = cur.indexOf(key);
    const j = i + dir;
    if (j < 0 || j >= cur.length) return;
    [cur[i], cur[j]] = [cur[j], cur[i]];
    saveOrder(cur);
  };
  const drop = (targetKey) => {
    if (dragKey && dragKey !== targetKey) {
      const cur = sorted.map((u) => u.key);
      const from = cur.indexOf(dragKey);
      const to = cur.indexOf(targetKey);
      cur.splice(from, 1);
      cur.splice(to, 0, dragKey);
      saveOrder(cur);
    }
    setDragKey(null);
    setOverKey(null);
  };
  const dp = (key) => ({
    isDragging: dragKey === key,
    isOver: !!dragKey && overKey === key && dragKey !== key,
    onDragStart: () => setDragKey(key),
    onDragEnd: () => { setDragKey(null); setOverKey(null); },
    onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setOverKey(key); },
    onDrop: (e) => { e.preventDefault(); drop(key); },
  });
  const createStack = () => {
    if (sel.length < 2) return;
    const id = `grp-${Date.now()}`;
    const cleaned = groups.map((g) => ({ ...g, members: g.members.filter((m) => !sel.includes(m)) })).filter((g) => g.members.length > 0);
    saveGroups([...cleaned, { id, title: stackName.trim() || "Section Stack", members: sel }]);
    const cur = sorted.map((u) => u.key);
    const first = cur.findIndex((k) => sel.includes(k));
    const next = cur.filter((k) => !sel.includes(k));
    next.splice(Math.max(0, first), 0, id);
    saveOrder(next);
    setSel([]); setStackName(""); setCombining(false);
  };
  const unstack = (g) => {
    saveGroups(groups.filter((x) => x.id !== g.id));
    const cur = sorted.map((u) => u.key);
    const i = cur.indexOf(g.id);
    const next = cur.filter((k) => k !== g.id);
    next.splice(i === -1 ? next.length : i, 0, ...g.members);
    saveOrder(next);
  };
  const removeFromStack = (g, m) => {
    const rest = g.members.filter((x) => x !== m);
    if (rest.length <= 1) return unstack(g);
    saveGroups(groups.map((x) => (x.id === g.id ? { ...x, members: rest } : x)));
    const cur = sorted.map((u) => u.key);
    const i = cur.indexOf(g.id);
    const next = [...cur];
    next.splice(i === -1 ? next.length : i + 1, 0, m);
    saveOrder(next);
  };
  return (
    <>
      <div className="mb-3" data-testid="section-stacker">
        {!combining ? (
          <div className="flex justify-end">
            <button data-testid="combine-sections-btn" onClick={() => setCombining(true)}
              className="flex items-center gap-1.5 border border-pb-cyan/50 text-pb-cyan text-[10px] font-black uppercase tracking-widest px-3 py-1.5 hover:bg-pb-cyan/10 transition-colors">
              <Layers className="w-3.5 h-3.5" /> Combine Sections
            </button>
          </div>
        ) : (
          <div className="border border-pb-cyan/50 bg-pb-cyan/5 p-4 animate-fade-up" data-testid="combine-panel">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-black uppercase tracking-[0.25em] text-pb-cyan flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5" /> Build a Stack — pick 2+ sections to combine into one cell
              </span>
              <button data-testid="combine-cancel" onClick={() => { setCombining(false); setSel([]); }}><X className="w-4 h-4 text-zinc-500 hover:text-white" /></button>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {kids.map((k) => {
                const on = sel.includes(k.props.testid);
                return (
                  <button key={k.props.testid} data-testid={`combine-pick-${k.props.testid}`}
                    onClick={() => setSel((s) => on ? s.filter((x) => x !== k.props.testid) : [...s, k.props.testid])}
                    className={`text-[9px] font-bold uppercase tracking-widest px-2.5 py-1.5 border transition-colors ${on ? "bg-pb-cyan text-black border-pb-cyan" : "border-pb-border text-zinc-400 hover:border-pb-cyan/60"}`}>
                    {k.props.title}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2">
              <input data-testid="combine-name-input" value={stackName} onChange={(e) => setStackName(e.target.value)}
                placeholder="Name this stack (e.g. Cards, Money, Approvals)…"
                className="flex-1 min-w-[200px] bg-pb-elevated border border-pb-border px-3 py-2 text-xs text-white focus:outline-none focus:border-pb-cyan" />
              <button data-testid="combine-create-btn" onClick={createStack} disabled={sel.length < 2}
                className="bg-pb-cyan text-black text-[10px] font-black uppercase tracking-widest px-4 py-2 hover:opacity-90 disabled:opacity-40 transition-all">
                Stack {sel.length || ""} Sections
              </button>
            </div>
          </div>
        )}
      </div>
      {sorted.map((u) => u.kid ? (
        React.cloneElement(u.kid, { key: u.key, onMove: move, dragProps: dp(u.key) })
      ) : (
        <AdminSection key={u.key} title={u.group.title} icon={Layers} testid={u.key} stack
          onMove={move} dragProps={dp(u.key)}
          badge={u.group.members.reduce((s, m) => s + (Number(kidByKey[m].props.badge) || 0), 0) || null}>
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid={`${u.key}-manage`}>
            <span className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 shrink-0">In this stack:</span>
            {u.group.members.map((m) => (
              <span key={m} className="flex items-center gap-1.5 border border-pb-border text-[9px] font-bold uppercase tracking-widest text-zinc-400 pl-2.5 pr-1.5 py-1">
                {kidByKey[m].props.title}
                <button data-testid={`${u.key}-remove-${m}`} title="Take out of this stack" onClick={() => removeFromStack(u.group, m)}
                  className="text-zinc-600 hover:text-pb-red"><X className="w-3 h-3" /></button>
              </span>
            ))}
            <button data-testid={`${u.key}-unstack`} onClick={() => unstack(u.group)}
              className="ml-auto text-[9px] font-black uppercase tracking-widest border border-pb-border text-zinc-500 px-2.5 py-1 hover:border-pb-red hover:text-pb-red transition-colors">
              Unstack All
            </button>
          </div>
          {u.group.members.map((m) => React.cloneElement(kidByKey[m], { key: m }))}
        </AdminSection>
      ))}
    </>
  );
}
